import { zodResolver } from '@hookform/resolvers/zod';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { PatternFormat } from 'react-number-format';

import { Loading } from '@/components/common/Loading';
import { Button } from '@/components/ui/button';
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';

import { useAuth } from '../hooks';
import { loginSchema, type LoginFormData } from '../schemas';

export function LoginForm() {
  const { t } = useTranslation(['auth', 'validation']);
  const { login, isLoggingIn } = useAuth();

  const {
    register,
    handleSubmit,
    setValue,
    formState: { errors },
  } = useForm<LoginFormData>({
    resolver: zodResolver(loginSchema),
    defaultValues: {
      phone: '',
      password: '',
    },
  });

  const onSubmit = (data: LoginFormData) => {
    // Remove formatting characters from phone
    const cleanPhone = data.phone.replace(/[+()\s]/g, '');
    login({ phone: cleanPhone, password: data.password });
  };

  return (
    <Card className="w-full max-w-sm">
      <CardHeader>
        <CardTitle className="text-2xl">{t('auth:login.title')}</CardTitle>
        <CardDescription>{t('auth:login.subtitle')}</CardDescription>
      </CardHeader>
      <CardContent>
        <form className="grid gap-4" onSubmit={handleSubmit(onSubmit)}>
          <div className="grid gap-2">
            <Label htmlFor="phone">{t('auth:login.phone')}</Label>
            <PatternFormat
              format="+998 (##) ### ## ##"
              allowEmptyFormatting
              mask="_"
              customInput={Input}
              id="phone"
              autoComplete="tel"
              onValueChange={(values) => {
                setValue('phone', values.formattedValue);
              }}
              className={errors.phone ? 'border-destructive' : ''}
            />
            {errors.phone && (
              <p className="text-sm text-destructive">{t(errors.phone.message ?? '')}</p>
            )}
          </div>

          <div className="grid gap-2">
            <Label htmlFor="password">{t('auth:login.password')}</Label>
            <Input
              id="password"
              type="password"
              autoComplete="current-password"
              {...register('password')}
              className={errors.password ? 'border-destructive' : ''}
            />
            {errors.password && (
              <p className="text-sm text-destructive">{t(errors.password.message ?? '')}</p>
            )}
          </div>

          <CardFooter className="px-0">
            <Button type="submit" className="w-full" disabled={isLoggingIn}>
              {isLoggingIn && <Loading size="sm" className="absolute left-4" />}
              {isLoggingIn ? t('auth:login.loading') : t('auth:login.submit')}
            </Button>
          </CardFooter>
        </form>
      </CardContent>
    </Card>
  );
}
